import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, User, Calendar as CalendarIcon, Target, TrendingUp, Lock, Users, Edit2 } from 'lucide-react';
import { useCoachClientView } from '../../hooks/useCoachClientView';
import GoalManagement from '../shared/GoalManagement';
import { HealthCalendarTile } from '../client/HealthCalendarTile';
import { EditClientModeModal } from './modals/EditClientModeModal';
import { ConfirmCoachedModeModal } from './modals/ConfirmCoachedModeModal';
import { ConfirmSelfManagedModal } from './modals/ConfirmSelfManagedModal';
import { CoachAssignmentModal } from './modals/CoachAssignmentModal';
import { RemoveCoachModal } from './modals/RemoveCoachModal';
import { EditClientPhaseModal } from './modals/EditClientPhaseModal';
import { ConfirmPhaseChangeModal } from './modals/ConfirmPhaseChangeModal';

/**
 * LEGACY CLIENT DASHBOARD VIEW
 *
 * This is the original client dashboard view preserved for reference.
 * New development should use ClientSummaryView.tsx instead.
 *
 * Access via: /staff/client/:clientId/legacy
 *
 * @deprecated Use ClientSummaryView for new features
 */
export const ClientDashboardViewLegacy: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientData, loading, error, refresh } = useCoachClientView(clientId!);

  // Modal state
  const [showEditModeModal, setShowEditModeModal] = useState(false);
  const [showConfirmCoachedModal, setShowConfirmCoachedModal] = useState(false);
  const [showConfirmSelfManagedModal, setShowConfirmSelfManagedModal] = useState(false);
  const [showAssignCoachModal, setShowAssignCoachModal] = useState(false);
  const [showRemoveCoachModal, setShowRemoveCoachModal] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<'assign' | 'change'>('assign');
  const [showEditPhaseModal, setShowEditPhaseModal] = useState(false);
  const [showConfirmPhaseModal, setShowConfirmPhaseModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<number>(0);

  // Coach assignment state
  const [assignedCoach, setAssignedCoach] = useState<{id: string; name: string} | null>(null);
  const [_loadingCoachInfo, setLoadingCoachInfo] = useState(false);

  // ESC key handler to return to client roster
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        navigate(-1); // Go back in browser history (preserves query params)
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Load assigned coach info
  useEffect(() => {
    if (clientData?.profile && !clientData.profile.self_managed) {
      loadAssignedCoach();
    } else {
      setAssignedCoach(null);
    }
  }, [clientData?.profile.self_managed, clientId]);

  const loadAssignedCoach = async () => {
    setLoadingCoachInfo(true);
    try {
      const token = localStorage.getItem('auth_token');
      // We'll need to add an endpoint to get coach info for a client
      // For now, we'll check if there's a relationship
      const response = await fetch(`/api/coach/clients/${clientId}/assigned-coach`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.coach) {
          setAssignedCoach({
            id: data.coach.coach_id,
            name: data.coach.name || data.coach.email
          });
        } else {
          setAssignedCoach(null);
        }
      }
    } catch (error) {
      console.error('Error loading coach info:', error);
      setAssignedCoach(null);
    } finally {
      setLoadingCoachInfo(false);
    }
  };

  // Handler functions
  const handleEditModeClick = () => {
    setShowEditModeModal(true);
  };

  const handleModeChange = (newMode: 'self-managed' | 'coached') => {
    setShowEditModeModal(false);

    if (newMode === 'coached') {
      setShowConfirmCoachedModal(true);
    } else {
      // Always show warning modal when switching to self-managed
      // Modal adapts based on whether coach is assigned (simple vs strong warning)
      setShowConfirmSelfManagedModal(true);
    }
  };

  const handleConfirmCoached = async (assignToMe: boolean) => {
    setShowConfirmCoachedModal(false);
    await updateClientMode(false);

    if (assignToMe) {
      await assignCoachToClient();
    }
  };

  const handleConfirmSelfManaged = async () => {
    setShowConfirmSelfManagedModal(false);
    await updateClientMode(true);
  };

  const updateClientMode = async (selfManaged: boolean) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/mode`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ self_managed: selfManaged })
      });

      if (response.ok) {
        await refresh();
        if (selfManaged) {
          setAssignedCoach(null);
        }
      } else {
        const data = await response.json();
        alert(`Failed to update client mode: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating client mode:', error);
      alert('Failed to update client mode');
    }
  };

  const handleAssignCoach = () => {
    setAssignmentMode('assign');
    setShowAssignCoachModal(true);
  };

  const handleChangeCoach = () => {
    setAssignmentMode('change');
    setShowAssignCoachModal(true);
  };

  const handleRemoveCoach = () => {
    setShowRemoveCoachModal(true);
  };

  const assignCoachToClient = async (coachId?: string) => {
    setShowAssignCoachModal(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ coach_id: coachId })
      });

      if (response.ok) {
        await loadAssignedCoach();
      } else {
        const data = await response.json();
        alert(`Failed to assign coach: ${data.error}`);
      }
    } catch (error) {
      console.error('Error assigning coach:', error);
      alert('Failed to assign coach');
    }
  };

  const removeCoachFromClient = async () => {
    setShowRemoveCoachModal(false);
    if (!assignedCoach) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/assign/${assignedCoach.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setAssignedCoach(null);
      } else {
        const data = await response.json();
        alert(`Failed to remove coach: ${data.error}`);
      }
    } catch (error) {
      console.error('Error removing coach:', error);
      alert('Failed to remove coach');
    }
  };

  // Phase Management Handlers
  const handleEditPhase = () => {
    setShowEditPhaseModal(true);
  };

  const handlePhaseSelection = (phase: number) => {
    setSelectedPhase(phase);
    setShowEditPhaseModal(false);
    setShowConfirmPhaseModal(true);
  };

  const handleConfirmPhaseChange = async () => {
    setShowConfirmPhaseModal(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/phase`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ client_phase: selectedPhase })
      });

      if (response.ok) {
        await refresh(); // Reload client data
      } else {
        const data = await response.json();
        alert(`Failed to update phase: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating phase:', error);
      alert('Failed to update phase');
    }
  };

  if (loading) {
    return (
      <div className="client-dashboard-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading client dashboard...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="client-dashboard-view">
        <div className="error-container">
          <h2>Error Loading Client Data</h2>
          <p>{error || 'Failed to load client information'}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={16} />
            Back to Client Roster
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const { profile, metrics, goals } = clientData;
  const activeGoals = goals.filter(g => g.status === 'enabled');

  return (
    <div className="client-dashboard-view">
      {/* Legacy View Banner */}
      <div className="legacy-banner">
        <span>Legacy View</span>
        <Link to={`/staff/client/${clientId}`} className="switch-view-link">
          Switch to New View
        </Link>
      </div>

      {/* Coach View Context Banner */}
      <div className="coach-context-banner">
        <div className="banner-content">
          <div className="banner-left">
            <button onClick={() => navigate(-1)} className="back-button">
              <ArrowLeft size={16} />
              Back to Clients
            </button>
            <div className="breadcrumbs">
              <Link to="/staff/clients">Client Roster</Link>
              <span className="separator">/</span>
              <span className="current">
                {profile.first_name} {profile.last_name}
              </span>
            </div>
          </div>
          <div className="view-indicator">
            <User size={16} />
            <span>Coach View (Read-Only)</span>
          </div>
        </div>
      </div>

      {/* Client Info Header */}
      <div className="client-info-header">
        <div className="client-avatar">
          {profile.first_name?.[0]}{profile.last_name?.[0]}
        </div>
        <div className="client-details">
          <h1>{profile.first_name} {profile.last_name}</h1>
          <div className="client-meta">
            <span className="meta-item">
              <User size={14} />
              Phase {profile.client_phase ?? 0}
            </span>
            <span className="meta-item">
              <Target size={14} />
              {activeGoals.length} Active Goal{activeGoals.length !== 1 ? 's' : ''}
            </span>
            {metrics && (
              <span className="meta-item">
                <TrendingUp size={14} />
                Score: {metrics.calculated_score || 'N/A'}
              </span>
            )}
            {metrics?.date && (
              <span className="meta-item">
                <CalendarIcon size={14} />
                Last Entry: {new Date(metrics.date).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="client-email">{profile.email}</div>
        </div>

        {/* Status Badge and Assignment Controls */}
        <div className="client-status-controls">
          <div className="status-badge-row">
            <span className={`status-badge ${profile.self_managed ? 'self-managed' : 'coached'}`}>
              {profile.self_managed ? (
                <>
                  <Lock size={14} />
                  Self-Managed
                </>
              ) : (
                <>
                  <Users size={14} />
                  Coached
                </>
              )}
            </span>
            <button className="edit-status-btn" onClick={handleEditModeClick}>
              <Edit2 size={14} />
              Change
            </button>
          </div>

          {/* Phase Badge Row */}
          <div className="status-badge-row">
            <span className="status-badge phase-badge">
              <TrendingUp size={14} />
              {profile.self_managed ? (
                'Phase 3 (Self-Managed)'
              ) : (
                `Phase ${profile.client_phase ?? 0}`
              )}
            </span>
            {!profile.self_managed && (
              <button className="edit-status-btn" onClick={handleEditPhase}>
                <Edit2 size={14} />
                Change Phase
              </button>
            )}
          </div>

          {/* Assignment Controls - Only show if coached */}
          {!profile.self_managed && (
            <div className="assignment-controls">
              {assignedCoach ? (
                <>
                  <span className="coach-label">Coach: {assignedCoach.name}</span>
                  <button className="btn-small btn-secondary" onClick={handleChangeCoach}>
                    Change
                  </button>
                  <button className="btn-small btn-danger-outline" onClick={handleRemoveCoach}>
                    Remove
                  </button>
                </>
              ) : (
                <button className="btn-small btn-primary" onClick={handleAssignCoach}>
                  Assign Coach
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${!location.pathname.includes('/goals') ? 'active' : ''}`}
          onClick={() => navigate(`/staff/client/${clientId}/legacy`)}
        >
          <CalendarIcon size={16} />
          Dashboard
        </button>
        <button
          className={`tab-button ${location.pathname.includes('/goals') ? 'active' : ''}`}
          onClick={() => navigate(`/staff/client/${clientId}/legacy/goals`)}
        >
          <Target size={16} />
          Goals
        </button>
      </div>

      {/* Client Dashboard Content */}
      <div className="dashboard-content">
        {location.pathname.includes('/goals') ? (
          <GoalManagement readOnly={profile.self_managed} userId={clientId} />
        ) : (
          <div className="calendar-wrapper">
            <HealthCalendarTile userId={clientId} readOnly={true} />
          </div>
        )}
      </div>

      {/* Modals */}
      <EditClientModeModal
        isOpen={showEditModeModal}
        onClose={() => setShowEditModeModal(false)}
        currentMode={profile.self_managed ? 'self-managed' : 'coached'}
        onSave={handleModeChange}
      />

      <ConfirmCoachedModeModal
        isOpen={showConfirmCoachedModal}
        onClose={() => setShowConfirmCoachedModal(false)}
        onConfirm={handleConfirmCoached}
        clientName={`${profile.first_name} ${profile.last_name}`}
      />

      <ConfirmSelfManagedModal
        isOpen={showConfirmSelfManagedModal}
        onClose={() => setShowConfirmSelfManagedModal(false)}
        onConfirm={handleConfirmSelfManaged}
        clientName={`${profile.first_name} ${profile.last_name}`}
        assignedCoaches={assignedCoach ? [{ coach_id: assignedCoach.id, name: assignedCoach.name }] : []}
      />

      <CoachAssignmentModal
        isOpen={showAssignCoachModal}
        onClose={() => setShowAssignCoachModal(false)}
        onAssign={assignCoachToClient}
        currentCoachId={assignedCoach?.id}
        clientName={`${profile.first_name} ${profile.last_name}`}
        mode={assignmentMode}
      />

      <RemoveCoachModal
        isOpen={showRemoveCoachModal}
        onClose={() => setShowRemoveCoachModal(false)}
        onConfirm={removeCoachFromClient}
        clientName={`${profile.first_name} ${profile.last_name}`}
        coachName={assignedCoach?.name || ''}
      />

      <EditClientPhaseModal
        isOpen={showEditPhaseModal}
        onClose={() => setShowEditPhaseModal(false)}
        onSelect={handlePhaseSelection}
        currentPhase={profile.client_phase ?? 0}
      />

      <ConfirmPhaseChangeModal
        isOpen={showConfirmPhaseModal}
        onClose={() => setShowConfirmPhaseModal(false)}
        onConfirm={handleConfirmPhaseChange}
        currentPhase={profile.client_phase ?? 0}
        newPhase={selectedPhase}
        clientName={`${profile.first_name} ${profile.last_name}`}
      />

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .client-dashboard-view {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
    background: var(--bg-primary);
  }

  .legacy-banner {
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: var(--radius-medium);
    padding: 8px 16px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    color: #92400e;
  }

  .legacy-banner span {
    font-weight: 600;
  }

  .switch-view-link {
    color: #d97706;
    text-decoration: underline;
    font-weight: 500;
  }

  .switch-view-link:hover {
    color: #b45309;
  }

  .coach-context-banner {
    background: linear-gradient(135deg, var(--accent-green), #059669);
    border-radius: var(--radius-large);
    padding: 16px 24px;
    margin-bottom: 24px;
    color: white;
  }

  .banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: var(--radius-medium);
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .back-button:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-2px);
  }

  .breadcrumbs {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }

  .breadcrumbs a {
    color: white;
    text-decoration: none;
    opacity: 0.8;
    transition: opacity 0.2s;
  }

  .breadcrumbs a:hover {
    opacity: 1;
    text-decoration: underline;
  }

  .breadcrumbs .separator {
    opacity: 0.5;
  }

  .breadcrumbs .current {
    font-weight: 600;
  }

  .view-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-medium);
    font-size: 13px;
    font-weight: 500;
  }

  .client-info-header {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 24px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    margin-bottom: 24px;
  }

  .client-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-green), #059669);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .client-details {
    flex: 1;
  }

  .client-details h1 {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .client-meta {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--text-secondary);
  }

  .meta-item svg {
    color: var(--accent-green);
  }

  .client-email {
    font-size: 14px;
    color: var(--text-secondary);
  }

  .tab-navigation {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    border-bottom: 2px solid var(--border-secondary);
    padding-bottom: 0;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -2px;
  }

  .tab-button:hover {
    color: var(--text-primary);
    background: var(--bg-secondary);
  }

  .tab-button.active {
    color: var(--accent-green);
    border-bottom-color: var(--accent-green);
  }

  .dashboard-content {
    background: var(--bg-primary);
  }

  .calendar-wrapper {
    max-width: 800px;
    margin: 0 auto;
  }

  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-container p,
  .error-container p {
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0;
  }

  .error-container h2 {
    color: var(--text-primary);
    margin: 0 0 12px;
  }

  .error-container .back-button {
    margin-top: 20px;
    background: var(--accent-green);
    border-color: var(--accent-green);
  }

  .error-container .back-button:hover {
    background: #059669;
  }

  @media (max-width: 768px) {
    .client-dashboard-view {
      padding: 12px;
    }

    .banner-content {
      flex-direction: column;
      align-items: flex-start;
    }

    .banner-left {
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
    }

    .client-info-header {
      flex-direction: column;
      text-align: center;
    }

    .client-meta {
      justify-content: center;
    }

    .tab-navigation {
      overflow-x: auto;
    }

    .tab-button {
      white-space: nowrap;
    }

    .client-status-controls {
      flex-direction: column;
      align-items: stretch;
    }

    .status-badge-row {
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .assignment-controls {
      flex-direction: column;
      gap: 8px;
    }

    .assignment-controls button {
      width: 100%;
    }
  }

  /* Phase 5A: Status Controls Styles */
  .client-status-controls {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-left: auto;
    padding-left: 24px;
  }

  .status-badge-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    border: 2px solid;
  }

  .status-badge.self-managed {
    background: rgba(249, 115, 22, 0.1);
    color: #ea580c;
    border-color: #ea580c;
  }

  .status-badge.coached {
    background: rgba(59, 130, 246, 0.1);
    color: #2563eb;
    border-color: #2563eb;
  }

  .edit-status-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s;
  }

  .edit-status-btn:hover {
    background: var(--hover);
    border-color: var(--primary);
  }

  .assignment-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .coach-label {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .btn-small {
    padding: 4px 10px;
    font-size: 12px;
    border-radius: 4px;
    border: 1px solid;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-small.btn-primary {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  .btn-small.btn-primary:hover {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }

  .btn-small.btn-secondary {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: var(--border);
  }

  .btn-small.btn-secondary:hover {
    background: var(--hover);
    border-color: var(--primary);
  }

  .btn-small.btn-danger-outline {
    background: transparent;
    color: var(--danger);
    border-color: var(--danger);
  }

  .btn-small.btn-danger-outline:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;
